import KopSurat from "./KopSurat";
import { useEffect, useState, useRef, useCallback } from "react";
import Bismillah from "./Bismillah";
import { Downloads } from "../../helper/DownloadService";
import FotoSantri from "./FotoSantri";
import LogoBaladz from "./LogoBaladz";
import KeputusanPenerimaan from "./KeputusanPenerimaan";
import { API_HOST, FOTO_HOST } from "../../helper/Constant";


function PDFCommon({ dataSantri, templateKey, onLoadComplete }) {
    const [renderedContent, setRenderedContent] = useState([]);
    const [fotoSantri, setFotoSantri] = useState(null);
    const loadedFotoRef = useRef(null);
    const isLoadingRef = useRef(false);
    const hasNotifiedRef = useRef(false);

    const fotoId = dataSantri?.data_berkas?.[0]?.foto_santri;

    useEffect(() => {
        hasNotifiedRef.current = false;

        if (!fotoId) {
            console.log("⏭️ No foto ID, skipping...");
            if (onLoadComplete && !hasNotifiedRef.current) {
                hasNotifiedRef.current = true;
                onLoadComplete(true);
            }
            return;
        }

        if (loadedFotoRef.current === fotoId) {
            console.log("✅ Foto already loaded, skipping...");
            if (onLoadComplete && !hasNotifiedRef.current) {
                hasNotifiedRef.current = true;
                onLoadComplete(true);
            }
            return;
        }

        if (isLoadingRef.current) {
            console.log("🔄 Currently loading, skipping...");
            return;
        }

        const fotoSantriUrl = `${API_HOST}/assets/${fotoId}`;
        
        isLoadingRef.current = true;
        console.log("📥 Fetching foto from:", fotoSantriUrl);
        
        Downloads.GetBase64Image(fotoSantriUrl)
            .then((base64Image) => {
                console.log("✅ Foto santri loaded successfully");
                setFotoSantri(base64Image);
                loadedFotoRef.current = fotoId;
                
                if (onLoadComplete && !hasNotifiedRef.current) {
                    hasNotifiedRef.current = true;
                    onLoadComplete(true);
                }
            })
            .catch((error) => {
                console.error("❌ Failed to load foto santri:", error);
                setFotoSantri(null);
                loadedFotoRef.current = null;
                
                if (onLoadComplete && !hasNotifiedRef.current) {
                    hasNotifiedRef.current = true;
                    onLoadComplete(false);
                }
            })
            .finally(() => {
                isLoadingRef.current = false;
            });
    }, [fotoId]); // ✅ REMOVE onLoadComplete from dependencies

    useEffect(() => {
        if (!dataSantri || !dataSantri.tahun_ajaran?.[templateKey]) {
            console.warn('Missing dataSantri or template');
            return;
        }

        console.log("🔄 Rendering template");

        const availableComponents = {
            KopSurat: {
                component: KopSurat,
                defaultProps: {}
            },
            Bismillah: {
                component: Bismillah,
                defaultProps: {}
            },
            FotoSantri: {
                component: FotoSantri,
                defaultProps: {
                    dataSantri: dataSantri,
                    foto: fotoSantri
                }
            },
            LogoBaladz: {
                component: LogoBaladz,
                defaultProps: {}
            },
            KeputusanPenerimaan: {
                component: KeputusanPenerimaan,
                defaultProps: {
                    dataSantri: dataSantri
                }
            },
        };

        try {
            const templateHtml = dataSantri.tahun_ajaran[templateKey];
            const parsedContent = parseTemplate(
                templateHtml,
                dataSantri,
                availableComponents
            );
            setRenderedContent(parsedContent);
        } catch (error) {
            console.error("Error rendering template:", error);
        }
    }, [dataSantri, templateKey, fotoSantri]);

    const parseTemplate = (html, data = {}, components = {}) => {
        if (!html) return [];

        data.ayah = data.data_ayah?.[0];
        data.ibu = data.data_ibu?.[0];
        data.wali = data.data_wali?.[0];
        data.sekolah = data.data_sekolah?.[0];
        data.tujuan = data.data_tujuan?.[0];
        data.lain = data.data_lain?.[0];
        data.berkas = data.data_berkas?.[0];
        data.foto_santri = fotoSantri;

        let processedHtml = html.replace(/{{(?!\/)(.*?)}}/g, (match, content) => {
            const trimmedContent = content.trim();

            if (/^[A-Z]/.test(trimmedContent)) {
                const parts = trimmedContent.match(/^(\w+)(?:\s+(.+))?$/);

                if (parts) {
                    const componentName = parts[1];
                    const propsString = parts[2] || '';

                    if (components[componentName]) {
                        const placeholder = `__COMPONENT_${componentName}_${Math.random().toString(36).substr(2, 9)}__`;

                        if (!window.__componentProps) {
                            window.__componentProps = {};
                        }
                        window.__componentProps[placeholder] = propsString;

                        return placeholder;
                    }
                }
            }

            if (trimmedContent.includes('.')) {
                const keys = trimmedContent.split('.');
                let value = data;

                for (const key of keys) {
                    value = value?.[key];
                }

                return value ?? '';
            }

            return data[trimmedContent] ?? '';
        });

        const parts = processedHtml.split(/(__COMPONENT_\w+_\w+__)/g);

        return parts.map((part, index) => {
            const componentMatch = part.match(/__COMPONENT_(\w+)_(\w+)__/);

            if (componentMatch) {
                const componentName = componentMatch[1];
                const componentConfig = components[componentName];

                if (componentConfig) {
                    const Component = componentConfig.component || componentConfig;
                    const defaultProps = componentConfig.defaultProps || {};

                    const propsString = window.__componentProps?.[part] || '';
                    const parsedProps = parseProps(propsString, data);

                    const finalProps = {
                        ...defaultProps,
                        ...parsedProps,
                        dataSantri: data,
                        fotoSantri: fotoSantri,
                        foto: fotoSantri,
                        key: `component-${index}`
                    };

                    return <Component {...finalProps} />;
                }
            }

            if (part.trim()) {
                return (
                    <div
                        key={`html-${index}`}
                        dangerouslySetInnerHTML={{ __html: part }}
                    />
                );
            }

            return null;
        }).filter(Boolean);
    };

    const parseProps = (propsString, data) => {
        const props = {};

        if (!propsString) return props;

        const propRegex = /(\w+)=(?:"([^"]*)"|'([^']*)'|{([^}]*)})/g;
        let match;

        while ((match = propRegex.exec(propsString)) !== null) {
            const key = match[1];
            const value = match[2] || match[3] || match[4];

            if (match[4]) {
                if (value.includes('.')) {
                    const keys = value.split('.');
                    let resolvedValue = data;
                    for (const k of keys) {
                        resolvedValue = resolvedValue?.[k];
                    }
                    props[key] = resolvedValue;
                } else {
                    props[key] = data[value];
                }
            } else {
                props[key] = value;
            }
        }

        return props;
    };

    if (!dataSantri) {
        return <div>Loading...</div>;
    }

    if (!dataSantri.tahun_ajaran?.[templateKey]) {
        return (
            <div>
                <p>Template tidak tersedia</p>
            </div>
        );
    }

    return (
        <div>
            {renderedContent.length > 0 ? (
                renderedContent
            ) : (
                <div>Loading template...</div>
            )}
        </div>
    );
}

export default PDFCommon;